"use client";
import Image from "next/image";
import { BsGithub, BsFacebook, BsLinkedin } from "react-icons/bs";
import { personalData } from "@/utils/data/personal-data";
import SectionHeading from "../../section-heading";
import { IconAward, IconBolt, IconSchool, IconUsers } from "@tabler/icons-react";

const stats = [
  {
    value: "150+",
    title: "Students mentored",
    description: "GDSC workshops covering HTML, CSS, JavaScript, and React.",
    icon: IconUsers,
    accent: "from-cyan-500/20 to-cyan-500/0 text-cyan-300",
  },
  {
    value: "30%",
    title: "Lighthouse gain",
    description: "Lazy loading and code-splitting for faster production UI.",
    icon: IconBolt,
    accent: "from-violet-500/20 to-violet-500/0 text-violet-300",
  },
  {
    value: "40%",
    title: "Booking efficiency",
    description: "Hotel booking redesign that made reservations easier to complete.",
    icon: IconAward,
    accent: "from-fuchsia-500/20 to-fuchsia-500/0 text-fuchsia-300",
  },
];

const AboutSection = () => {
  return (
    <section id="about" className="relative py-24 px-4">
      <SectionHeading
        eyebrow="[About]"
        title="Who"
        highlight="I am"
        description="Frontend-focused engineer with production SaaS, GRC, and mentoring experience."
      />

      <div className="max-w-6xl mx-auto space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 rounded-2xl border border-white/10 bg-neutral-950/80 p-6 md:p-8">
          <div className="relative overflow-hidden rounded-2xl border border-white/10">
            <Image
              src={personalData.profile}
              alt={personalData.name}
              width={560}
              height={700}
              className="h-full w-full min-h-[280px] object-cover"
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4">
              <p className="text-sm text-neutral-200">
                {personalData.designation}
              </p>
              <p className="text-xs text-neutral-400">{personalData.address}</p>
            </div>
          </div>

          <div className="flex flex-col justify-center">
            <p className="text-xs uppercase tracking-[0.28em] text-violet-400 mb-3">
              About me
            </p>
            <h3 className="text-3xl md:text-4xl font-bold text-white">
              {personalData.name}
            </h3>
            <p className="mt-4 text-neutral-300 leading-relaxed">
              {personalData.about}
            </p>

            <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.03] p-4 flex items-start gap-3">
              <div className="rounded-lg bg-cyan-500/10 p-2">
                <IconSchool className="h-5 w-5 text-cyan-300" />
              </div>
              <div>
                <p className="font-semibold text-white">{personalData.education.degree}</p>
                <p className="text-sm text-neutral-400 mt-1">
                  {personalData.education.school}
                </p>
                <p className="text-sm text-neutral-500 mt-1">
                  GPA {personalData.education.gpa} · {personalData.education.year}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6">
              {[
                { href: personalData.github, icon: BsGithub, label: "GitHub" },
                { href: personalData.linkedIn, icon: BsLinkedin, label: "LinkedIn" },
                { href: personalData.facebook, icon: BsFacebook, label: "Facebook" },
              ].map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={social.label}
                  className="rounded-full border border-white/10 bg-white/5 p-3 text-neutral-300 hover:text-white hover:border-violet-400/50 transition-colors"
                >
                  <social.icon className="text-xl" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {stats.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.title}
                className={`rounded-2xl border border-white/10 bg-gradient-to-b ${item.accent} p-6`}
              >
                <Icon className="h-5 w-5 mb-4" />
                <p className="text-4xl font-bold text-white">{item.value}</p>
                <p className="mt-2 font-semibold text-white">{item.title}</p>
                <p className="mt-2 text-sm text-neutral-400">{item.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
